import { Recording } from "@/models/recording";
import { RecordStatusEnum } from "@/pages/RecordsList/types";
import { useCallback, useState } from "react";
import { RowSelectedType, RowSelectionContext, RowSelectionContextType } from "./RowSelectionContext";

export interface RowSelectionProviderProps {
  children: React.ReactNode;
}

export function RowSelectionProvider({ children }: RowSelectionProviderProps) {
  const [selecteds, setSelecteds] = useState(new Map<string, RowSelectedType>());
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const isRowSelected = (item: string) => {
    if (!item) return false;
    return selecteds.has(item);
  };

  const onSelect = (key: string, value?: RowSelectedType) => {
    if (isRowSelected(key)) {
      setSelecteds((prevState) => {
        const newSelecteds = new Map(prevState);
        newSelecteds.delete(key);
        return newSelecteds;
      });
    } else if (value) {
      setSelecteds((prevState) => {
        const newSelecteds = new Map(prevState);
        newSelecteds.set(key, value);
        return newSelecteds;
      });
    }
  };

  const onSelectAll = (items: RowSelectedType[], selectAll: boolean) => {
    setSelecteds((prevState) => {
      const newSelecteds = new Map(prevState);
      items.forEach((item) => (selectAll ? newSelecteds.set(item.row, item) : newSelecteds.delete(item.row)));
      return newSelecteds;
    });
  };

  const resetSelection = useCallback(() => setSelecteds(new Map()), []);

  const onAddExpandedRow = (recordingId: string) => {
    setExpandedRows((oldValue) => {
      const expandedRowsFiltered = expandedRows.includes(recordingId) ? expandedRows.filter((id) => id !== recordingId) : [...oldValue, recordingId];
      return expandedRowsFiltered;
    });
  };

  const isSelectDisabled = useCallback((recording: Recording) => {
    const { status, isUploadInterrupted } = recording;
    const readyStatus = new Set([RecordStatusEnum.DONE, RecordStatusEnum.READY_TO_EDIT, RecordStatusEnum.READY_TO_EXPORT, RecordStatusEnum.GENERATED]);
    const isReady = readyStatus.has(status);
    const isDone = status === RecordStatusEnum.DONE;
    const hasFailed = status === RecordStatusEnum.FAILED;
    const isPaused = status === RecordStatusEnum.PAUSED;
    const isRecording = status === RecordStatusEnum.RECORDING;
    return isUploadInterrupted || !(isReady || isDone || hasFailed) || isPaused || isRecording;
  }, []);

  const onResetExpandedRow = () => setExpandedRows([]);

  const value: RowSelectionContextType = {
    selecteds,
    onSelect,
    onSelectAll,
    resetSelection,
    isRowSelected,
    expandedRows,
    onAddExpandedRow,
    onResetExpandedRow,
    isSelectDisabled,
  };

  return <RowSelectionContext value={value}>{children}</RowSelectionContext>;
}
